import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatCardNumber, formatExpiry, maskedCardNumber } from '@heybesafe/shared';
import { useSafe } from '../context/SafeContext';
import LoadingPane from '../components/LoadingPane';
import { HbsInput } from '../components/hbs/Input';
import { HbsButton } from '../components/hbs/Button';
import { Icon } from '../components/hbs/Icon';
import { EyeToggle } from '../components/hbs/EyeToggle';
import { SecretValue } from '../components/hbs/SecretValue';
import { cardBrandLabel } from '../lib/cardBrandLabel';

/**
 * How long a revealed card number / CVV stays visible before auto-hiding. Hard
 * timer — does NOT reset on activity. Mirrors the password reveal window in
 * ItemDetailPage.
 */
const CARD_REVEAL_MS = 60 * 1000;

function CopyAction({ value, label }: { value: string; label: string }): JSX.Element {
  return (
    <button
      // One per field, so the fixed internal field label namespaces the id.
      id={`card-copy-${label.toLowerCase().replace(/\s+/g, '-')}-button`}
      type="button"
      aria-label={`Copy ${label}`}
      title={`Copy ${label}`}
      onClick={() => void navigator.clipboard.writeText(value)}
      className="p-1 rounded-hbs-sm text-hbs-text-mute hover:text-hbs-text hover:bg-hbs-surface-alt"
    >
      <Icon name="copy" size={16} />
    </button>
  );
}

export default function CardDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { items, itemsLoaded, deleteItem } = useSafe();
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [numberRevealed, setNumberRevealed] = useState(false);
  const [cvvRevealed, setCvvRevealed] = useState(false);

  // Auto-hide the number / CVV 60s after reveal. Hard timers (no activity reset).
  useEffect(() => {
    if (!numberRevealed) return;
    const t = setTimeout(() => setNumberRevealed(false), CARD_REVEAL_MS);
    return () => clearTimeout(t);
  }, [numberRevealed]);
  useEffect(() => {
    if (!cvvRevealed) return;
    const t = setTimeout(() => setCvvRevealed(false), CARD_REVEAL_MS);
    return () => clearTimeout(t);
  }, [cvvRevealed]);

  const item = items.find((i) => i.id === id && i.type === 'card' && !i.orgId);

  if (!item) {
    // items is emptied on lock and only repopulated by the post-unlock
    // syncPull, so an absent item means loading until itemsLoaded.
    if (!itemsLoaded) return <LoadingPane label="Loading payment card…" />;
    return (
      <div className="h-full flex flex-col items-center justify-center text-hbs-text-mute gap-3">
        <p className="text-[14px]">Payment card not found.</p>
        <HbsButton variant="plain" id="card-back-button" onClick={() => navigate('/cards')}>
          Back to payment cards
        </HbsButton>
      </div>
    );
  }

  const brand = cardBrandLabel(item.cardBrand);
  const digits = item.cardNumber ?? '';
  const masked = maskedCardNumber(digits);
  const expiry = formatExpiry(item.expMonth, item.expYear);

  async function handleDelete(): Promise<void> {
    setDeleting(true);
    try {
      await deleteItem(item!.id);
      navigate('/cards');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="p-7 flex flex-col gap-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-start gap-4">
        <span className="shrink-0 w-16 h-16 rounded-hbs-xl bg-hbs-surface-alt flex items-center justify-center">
          <Icon name="card" size={32} className="text-hbs-text-mute" />
        </span>
        <div className="flex-1 min-w-0">
          <h2 className="font-display font-semibold text-[30px] tracking-hbs-tighter text-hbs-text truncate">
            {item.name}
          </h2>
          <p className="text-[13px] text-hbs-text-mute mt-1">
            Payment Card{brand && ` · ${brand}`}
          </p>
        </div>
      </div>

      {/* Details card — no website / "Launch & autofill": meaningless for a card. */}
      <section className="bg-hbs-surface border border-hbs-border rounded-hbs-xl p-6">
        <div className="flex flex-col gap-4">
          {digits && (
            <HbsInput
              label="Card number"
              value={numberRevealed ? formatCardNumber(digits, item.cardBrand) : masked}
              onChange={() => {}}
              readOnly
              mono
              renderValue={numberRevealed ? (v) => <SecretValue value={v} /> : undefined}
              trailingActions={
                <>
                  <EyeToggle revealed={numberRevealed} onChange={setNumberRevealed} label="card number" />
                  <CopyAction value={digits} label="card number" />
                </>
              }
            />
          )}
          {expiry && (
            <HbsInput
              label="Expiry"
              value={expiry}
              onChange={() => {}}
              readOnly
              trailingActions={<CopyAction value={expiry} label="expiry" />}
            />
          )}
          {item.cardholderName && (
            <HbsInput
              label="Cardholder name"
              value={item.cardholderName}
              onChange={() => {}}
              readOnly
              trailingActions={<CopyAction value={item.cardholderName} label="cardholder name" />}
            />
          )}
          {item.cardSecurityCode && (
            <HbsInput
              label="Security code (CVV)"
              value={item.cardSecurityCode}
              onChange={() => {}}
              readOnly
              type={cvvRevealed ? 'text' : 'password'}
              mono={cvvRevealed}
              renderValue={cvvRevealed ? (v) => <SecretValue value={v} /> : undefined}
              trailingActions={
                <>
                  <EyeToggle revealed={cvvRevealed} onChange={setCvvRevealed} label="security code" />
                  <CopyAction value={item.cardSecurityCode} label="security code" />
                </>
              }
            />
          )}
          {item.cardBillingZip && (
            <HbsInput
              label="Billing ZIP"
              value={item.cardBillingZip}
              onChange={() => {}}
              readOnly
              trailingActions={<CopyAction value={item.cardBillingZip} label="billing zip" />}
            />
          )}
        </div>

        <div className="mt-6 flex justify-between items-center">
          <div className="flex items-center gap-1">
            <HbsButton variant="plain" id="card-edit-button" onClick={() => navigate(`/cards/${item.id}/edit`)}>
              Edit
            </HbsButton>
          </div>
          {!confirming ? (
            <HbsButton variant="plain" id="card-delete-button" onClick={() => setConfirming(true)}>
              Delete
            </HbsButton>
          ) : (
            <div className="flex gap-2">
              <HbsButton variant="ghost" id="card-delete-cancel-button" onClick={() => setConfirming(false)}>
                Cancel
              </HbsButton>
              <HbsButton variant="accent" loading={deleting} id="card-delete-confirm-button" onClick={handleDelete}>
                Confirm
              </HbsButton>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
